<template>
  <div class="dash">
    <!-- Header ringkas: status koneksi & waktu -->
    <div class="dash-topbar">
      <div class="dash-welcome">
        <div class="dash-welcome-title">
          Selamat Datang, {{ user.name ? user.name.toUpperCase() : "" }}
        </div>
        <div class="dash-welcome-subtitle">
          Dashboard Pemantauan CCTV Kabupaten Tangerang
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="dash-status">
        <v-icon
          small
          color="#22c55e"
        >mdi-shield-check</v-icon>
        <div class="dash-status-text">
          <div class="dash-status-label">TERHUBUNG</div>
          <div class="dash-status-value">{{ stats.online }} Kamera Online</div>
        </div>
      </div>
      <div class="dash-clock">
        <v-icon
          small
          color="#93c5fd"
        >mdi-calendar-clock</v-icon>
        <div class="dash-status-text">
          <div class="dash-status-label">{{ nowDate }}</div>
          <div class="dash-status-value">{{ nowTime }} WIB</div>
        </div>
      </div>
    </div>

    <v-row dense>
      <!-- PETA SEBARAN CCTV -->
      <v-col
        :cols="device.mobile ? 12 : 7"
        class="d-flex"
      >
        <div class="dash-panel flex-grow-1">
          <div class="dash-panel-header">
            <span class="dash-panel-title">PETA SEBARAN CCTV</span>
          </div>
          <div class="dash-map-wrapper">
            <l-map
              style="height: 420px; z-index: 0;"
              :zoom="zoom"
              :center="center"
            >
              <l-tile-layer
                :url="url"
                :attribution="attribution"
              ></l-tile-layer>
              <l-marker
                v-for="(item, index) in markers"
                :key="index"
                :visible="item.visible"
                :draggable="item.draggable"
                :lat-lng.sync="item.position"
                @click="openCamera"
              >
                <l-icon
                  :icon-url="item.icon"
                  :icon-size="item.size || [36, 48]"
                  :icon-anchor="item.anchor || [18, 46]"
                  :popup-anchor="[0, -40]"
                />
                <l-popup :content="item.tooltip" />
                <l-tooltip :content="item.tooltip" />
              </l-marker>

              <l-circle
                v-if="selectedMarkerPosition"
                :lat-lng="selectedMarkerPosition"
                :radius="rippleRadiusPrimary"
                color="#60a5fa"
                :weight="2"
                :opacity="rippleOpacityPrimary"
                :fill-opacity="0"
              />
              <l-circle
                v-if="selectedMarkerPosition"
                :lat-lng="selectedMarkerPosition"
                :radius="rippleRadiusSecondary"
                color="#93c5fd"
                :weight="2"
                :opacity="rippleOpacitySecondary"
                :fill-opacity="0"
              />
              <l-circle
                v-if="selectedMarkerPosition"
                :lat-lng="selectedMarkerPosition"
                :radius="56"
                color="#3b82f6"
                :weight="2"
                :opacity="0.9"
                :fill="true"
                :fill-opacity="0.15"
              />
            </l-map>
          </div>
          <div class="dash-map-stats">
            <div class="dash-map-stat">
              <v-icon
                small
                color="#93c5fd"
              >mdi-cctv</v-icon>
              <div>
                <div class="dash-map-stat-label">Total Kamera</div>
                <div class="dash-map-stat-value">{{ stats.total }}</div>
              </div>
            </div>
            <div class="dash-map-stat">
              <v-icon
                small
                color="#22c55e"
              >mdi-video</v-icon>
              <div>
                <div class="dash-map-stat-label">Online</div>
                <div class="dash-map-stat-value online">{{ stats.online }}</div>
              </div>
            </div>
            <div class="dash-map-stat">
              <v-icon
                small
                color="#ef4444"
              >mdi-video-off</v-icon>
              <div>
                <div class="dash-map-stat-label">Offline</div>
                <div class="dash-map-stat-value offline">{{ stats.offline }}</div>
              </div>
            </div>
            <div class="dash-map-stat">
              <v-icon
                small
                color="#f59e0b"
              >mdi-alert-outline</v-icon>
              <div>
                <div class="dash-map-stat-label">Gangguan</div>
                <div class="dash-map-stat-value gangguan">{{ stats.gangguan }}</div>
              </div>
            </div>
          </div>
        </div>
      </v-col>

      <!-- LIVE VIEW -->
      <v-col
        :cols="device.mobile ? 12 : 5"
        class="d-flex"
      >
        <div class="dash-panel flex-grow-1">
          <div class="dash-panel-header">
            <span class="dash-panel-title">LIVE VIEW</span>
            <v-spacer></v-spacer>
            <span
              class="dash-panel-link"
              @click="$router.push({ name: 'pemantauan-cctv' })"
            >Lihat Semua →</span>
          </div>
          <div
            class="dash-live-grid"
            v-if="cameras.length"
          >
            <div
              class="dash-live-tile"
              v-for="(cam, idx) in cameras"
              :key="idx"
              :class="{ 'dash-live-tile--active': selectedMarkerId === cam.id }"
              @mouseenter="focusCameraOnMap(cam)"
              @click="$router.push({ name: 'pemantauan-cctv' })"
            >
              <img
                v-if="liveThumb(cam)"
                :src="liveThumb(cam)"
                class="dash-live-img"
                alt=""
                @error="onLiveThumbError(cam.id)"
              />
              <div
                v-else
                class="dash-live-placeholder"
              >
                <v-icon
                  large
                  color="#334a77"
                >mdi-cctv</v-icon>
              </div>
              <div class="dash-live-caption">
                <span
                  class="dash-dot"
                  :class="cam.status === 'baik' ? 'dash-dot--green' : 'dash-dot--red'"
                ></span>
                <span class="dash-live-name">{{ cam.area }} - {{ cam.name }}</span>
                <v-icon
                  x-small
                  color="#94a3b8"
                  class="ml-auto"
                >mdi-fullscreen</v-icon>
              </div>
            </div>
          </div>
          <div
            v-else
            class="dash-empty"
          >
            <v-icon
              large
              color="#334a77"
            >mdi-video-off-outline</v-icon>
            <div class="dash-empty-title">Belum ada kamera</div>
            <div class="dash-empty-subtitle">Tambahkan kamera pada menu Master CCTV</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row dense>
      <!-- STATISTIK CCTV -->
      <v-col
        :cols="device.mobile ? 12 : 3"
        class="d-flex"
      >
        <div class="dash-panel flex-grow-1">
          <div class="dash-panel-header">
            <span class="dash-panel-title">STATISTIK CCTV</span>
          </div>
          <div class="dash-donut-wrapper">
            <div
              class="dash-donut"
              :style="donutStyle"
            >
              <div class="dash-donut-hole">
                <div class="dash-donut-total">{{ stats.total }}</div>
                <div class="dash-donut-label">Total Kamera</div>
              </div>
            </div>
            <div class="dash-legend">
              <div class="dash-legend-item">
                <span class="dash-dot dash-dot--green"></span>
                Online <b>{{ stats.online }} ({{ percent(stats.online) }}%)</b>
              </div>
              <div class="dash-legend-item">
                <span class="dash-dot dash-dot--red"></span>
                Offline <b>{{ stats.offline }} ({{ percent(stats.offline) }}%)</b>
              </div>
              <div class="dash-legend-item">
                <span class="dash-dot dash-dot--amber"></span>
                Gangguan <b>{{ stats.gangguan }} ({{ percent(stats.gangguan) }}%)</b>
              </div>
            </div>
          </div>
        </div>
      </v-col>

      <!-- TOP 5 WILAYAH -->
      <v-col
        :cols="device.mobile ? 12 : 3"
        class="d-flex"
      >
        <div class="dash-panel flex-grow-1">
          <div class="dash-panel-header">
            <span class="dash-panel-title">TOP 5 WILAYAH DENGAN KAMERA TERBANYAK</span>
          </div>
          <div
            class="dash-top-list"
            v-if="topAreas.length"
          >
            <div
              class="dash-top-item"
              v-for="(area, idx) in topAreas"
              :key="idx"
            >
              <div class="dash-top-row">
                <span class="dash-top-name">{{ idx + 1 }}. {{ area.name }}</span>
                <span class="dash-top-count">{{ area.count }}</span>
              </div>
              <div class="dash-top-bar">
                <div
                  class="dash-top-bar-fill"
                  :style="{ width: barWidth(area.count) }"
                ></div>
              </div>
            </div>
          </div>
          <div
            v-else
            class="dash-empty"
          >
            <v-icon
              large
              color="#334a77"
            >mdi-map-marker-off-outline</v-icon>
            <div class="dash-empty-title">Belum ada data wilayah</div>
          </div>
          <div
            class="dash-panel-footer"
            @click="$router.push({ name: 'master-cctv' })"
          >Lihat Semua</div>
        </div>
      </v-col>

      <!-- TOP CCTV DIAKSES -->
      <v-col
        :cols="device.mobile ? 12 : 3"
        class="d-flex"
      >
        <div class="dash-panel flex-grow-1">
          <div class="dash-panel-header">
            <span class="dash-panel-title">CCTV PALING SERING DIAKSES</span>
          </div>
          <div
            class="dash-top-list"
            v-if="topAccessed.length"
          >
            <div
              class="dash-top-item"
              v-for="(cam, idx) in topAccessed"
              :key="cam.id || idx"
            >
              <div class="dash-top-row">
                <span class="dash-top-name">{{ idx + 1 }}. {{ cam.name }}</span>
                <span class="dash-top-count">{{ cam.count }}x</span>
              </div>
              <div class="dash-top-area">{{ cam.area }}</div>
              <div class="dash-top-bar">
                <div
                  class="dash-top-bar-fill dash-top-bar-fill--access"
                  :style="{ width: accessBarWidth(cam.count) }"
                ></div>
              </div>
            </div>
          </div>
          <div
            v-else
            class="dash-empty"
          >
            <v-icon
              large
              color="#334a77"
            >mdi-chart-bar</v-icon>
            <div class="dash-empty-title">Belum ada data akses</div>
            <div class="dash-empty-subtitle">Data muncul setelah kamera diputar</div>
          </div>
          <div
            class="dash-panel-footer"
            @click="$router.push({ name: 'pemantauan-cctv' })"
          >Lihat Semua</div>
        </div>
      </v-col>

      <!-- KAMERA GANGGUAN -->
      <v-col
        :cols="device.mobile ? 12 : 3"
        class="d-flex"
      >
        <div class="dash-panel flex-grow-1">
          <div class="dash-panel-header">
            <span class="dash-panel-title">KAMERA GANGGUAN</span>
          </div>
          <div
            class="dash-event-list"
            v-if="gangguan.length"
          >
            <div
              class="dash-event-item"
              v-for="(cam, idx) in gangguan"
              :key="idx"
            >
              <div
                class="dash-event-icon"
                style="background: #7f1d1d;"
              >
                <v-icon
                  small
                  color="white"
                >mdi-video-off</v-icon>
              </div>
              <div class="dash-event-text">
                <div class="dash-event-title">{{ cam.name }}</div>
                <div class="dash-event-subtitle">{{ cam.area }} - {{ cam.address }}</div>
              </div>
            </div>
          </div>
          <div
            v-else
            class="dash-empty"
          >
            <div class="dash-empty-cam">
              <v-icon
                large
                color="#3b5689"
              >mdi-video-off-outline</v-icon>
            </div>
            <div class="dash-empty-title green--text">Tidak ada kamera gangguan</div>
            <div class="dash-empty-subtitle">Semua kamera berfungsi normal</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-dialog v-model="updateDialog" persistent max-width="440">
      <v-card dark class="dash-update-card">
        <div class="dash-update-head">
          <v-icon color="#60a5fa" size="40">mdi-update</v-icon>
          <div class="dash-update-title">Pembaruan Aplikasi Tersedia</div>
          <div class="dash-update-subtitle">
            Versi aplikasi Anda <b>{{ appVersion }}</b>, versi terbaru
            <b>{{ serverVersion }}</b>. Silakan lakukan pembaruan untuk
            melanjutkan.
          </div>
        </div>

        <div v-if="updating" class="px-6 pb-2">
          <v-progress-linear
            :value="updateProgress"
            color="#60a5fa"
            background-color="#1e2c4f"
            height="10"
            rounded
          ></v-progress-linear>
          <div class="dash-update-status">
            {{ updateStatus }} ({{ updateProgress }}%)
          </div>
        </div>

        <v-card-actions class="px-6 pb-5">
          <v-btn
            block
            color="#2563eb"
            depressed
            :loading="updating"
            :disabled="updating"
            @click="runUpdate"
          >
            <v-icon left>mdi-download</v-icon>
            Update Sekarang
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import {
  LMap,
  LTileLayer,
  LMarker,
  LIcon,
  LPopup,
  LTooltip,
  LCircle,
} from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import { domainURL, mapTile, appVersion } from "../../../.env";

export default {
  name: "dashboard",

  components: {
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LPopup,
    LTooltip,
    LCircle,
  },

  data: () => ({
    //map property
    url: mapTile.URL,
    attribution: mapTile.ATTRIBUTION,
    zoom: 11,
    center: [-6.2204, 106.4675],
    brokenThumbs: {},
    selectedMarkerId: null,
    selectedMarkerPosition: null,
    rippleStep: 0,
    rippleTimer: null,

    markers: [],
    stats: {
      total: 0,
      online: 0,
      offline: 0,
      gangguan: 0,
    },
    cameras: [],
    topAreas: [],
    topAccessed: [],
    gangguan: [],

    now: new Date(),
    timer: null,

    appVersion,
    serverVersion: "",
    updateDialog: false,
    updating: false,
    updateProgress: 0,
    updateStatus: "",
  }),
  computed: {
    ...mapState(["theme", "http", "device", "loading", "event", "user"]),

    nowDate() {
      return this.now.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    },
    nowTime() {
      return this.now.toLocaleTimeString("id-ID", { hour12: false });
    },
    donutStyle() {
      if (!this.stats.total) {
        return { background: "#1e2c4f" };
      }
      const total = this.stats.total;
      const online = (this.stats.online / total) * 100;
      const offline = online + (this.stats.offline / total) * 100;
      return {
        background: `conic-gradient(#22c55e 0% ${online}%, #ef4444 ${online}% ${offline}%, #f59e0b ${offline}% 100%)`,
      };
    },
    rippleRadiusPrimary() {
      return 140 + this.rippleStep * 14;
    },
    rippleRadiusSecondary() {
      const phase = (this.rippleStep + 50) % 100;
      return 140 + phase * 14;
    },
    rippleOpacityPrimary() {
      return Math.max(0.06, 0.5 - this.rippleStep * 0.004);
    },
    rippleOpacitySecondary() {
      const phase = (this.rippleStep + 50) % 100;
      return Math.max(0.06, 0.5 - phase * 0.004);
    },
  },
  created() {
    this.setPage({
      crud: true,
      dataUrl: "api/dashboard",
      pagination: false,
      title: "",
      subtitle: "",
      showtable: false,
      breadcrumbs: [],
    });

    this.fetchDashboard();
    this.checkVersion();
  },
  mounted() {
    this.timer = setInterval(() => {
      this.now = new Date();
    }, 1000);
    this.startRippleAnimation();
  },
  beforeDestroy() {
    clearInterval(this.timer);
    if (this.rippleTimer) {
      clearInterval(this.rippleTimer);
      this.rippleTimer = null;
    }
  },
  methods: {
    ...mapActions(["setPage"]),

    fetchDashboard: async function () {
      let {
        data: { cctvs, stats, cameras, top_areas, top_accessed, gangguan },
      } = await this.http.get("api/dashboard");

      this.markers = cctvs || [];
      this.stats = stats || this.stats;
      this.cameras = cameras || [];
      this.topAreas = top_areas || [];
      this.topAccessed = top_accessed || [];
      this.gangguan = gangguan || [];
    },

    percent: function (value) {
      if (!this.stats.total) return 0;
      return Math.round((value / this.stats.total) * 1000) / 10;
    },

    barWidth: function (count) {
      const max = this.topAreas.length ? this.topAreas[0].count : 1;
      return Math.round((count / max) * 100) + "%";
    },

    accessBarWidth: function (count) {
      const max = this.topAccessed.length ? this.topAccessed[0].count : 1;
      return Math.round((count / Math.max(max, 1)) * 100) + "%";
    },

    fotoSrc: function (foto) {
      if (!foto) return null;
      const raw = String(foto).trim();
      if (!raw) return null;
      if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;

      const httpBase = (
        (this.http && this.http.defaults && this.http.defaults.baseURL) ||
        domainURL.BASE ||
        ""
      ).replace(/\/$/, "");
      const origin =
        httpBase.replace(/\/api$/i, "") ||
        (typeof window !== "undefined" ? window.location.origin : "");
      const [pathPart, query] = raw.split("?");
      const q = query ? `?${query}` : "";
      const clean = pathPart.replace(/^\//, "");

      if (clean.startsWith("images/")) {
        return `${origin}/${clean}${q}`;
      }
      if (clean.startsWith("storage/cctv-captures/")) {
        return `${httpBase}/api/${clean}${q}`;
      }
      if (pathPart.startsWith("/")) {
        return `${origin}${pathPart}${q}`;
      }
      return null;
    },

    liveThumb: function (cam) {
      if (!cam || this.brokenThumbs[cam.id]) return null;
      return this.fotoSrc(cam.foto);
    },

    onLiveThumbError: function (id) {
      if (!id) return;
      this.$set(this.brokenThumbs, id, true);
    },

    startRippleAnimation: function () {
      if (this.rippleTimer) return;
      this.rippleTimer = setInterval(() => {
        this.rippleStep = (this.rippleStep + 2) % 100;
      }, 80);
    },

    focusCameraOnMap: function (cam) {
      if (!cam) return;
      let lat = cam.lat != null ? parseFloat(cam.lat) : NaN;
      let lng = cam.lng != null ? parseFloat(cam.lng) : NaN;

      if (isNaN(lat) || isNaN(lng)) {
        const marker = this.markers.find((m) => m.id === cam.id);
        if (!marker || !marker.position) return;
        lat = parseFloat(marker.position.lat);
        lng = parseFloat(marker.position.lng);
      }
      if (isNaN(lat) || isNaN(lng)) return;

      this.selectedMarkerId = cam.id;
      this.selectedMarkerPosition = { lat, lng };
      this.center = [lat, lng];
      this.rippleStep = 0;
    },

    openCamera: async function () {
      this.$router.push({ name: "pemantauan-cctv" });
    },

    checkVersion: async function () {
      try {
        const { data } = await this.http.get("api/info", {
          params: { t: Date.now() },
        });
        this.serverVersion = String(data.app_ver || "").trim();
        const localVersion =
          localStorage.getItem("app_version") || appVersion.trim();
        if (this.serverVersion && this.serverVersion !== localVersion) {
          this.appVersion = localVersion;
          this.updateDialog = true;
        }
      } catch (error) {}
    },

    runUpdate: async function () {
      this.updating = true;
      const step = (progress, status) => {
        this.updateProgress = progress;
        this.updateStatus = status;
        return new Promise((resolve) => setTimeout(resolve, 400));
      };

      try {
        await step(10, "Menyiapkan pembaruan...");
        if ("serviceWorker" in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }

        await step(40, "Membersihkan cache aplikasi...");
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
        sessionStorage.clear();

        await step(70, "Mengunduh versi terbaru...");
        await fetch(window.location.href, { cache: "reload" });

        await step(100, "Selesai, memuat ulang aplikasi...");
      } catch (error) {
        await step(100, "Memuat ulang aplikasi...");
      }
      localStorage.setItem("app_version", this.serverVersion);
      window.location.reload();
    },
  },
};
</script>

<style scoped>
.dash {
  min-height: 100%;
  background: #0a1224;
  padding: 16px;
}
.dash-update-card {
  background: #0f1a33 !important;
  border: 1px solid #1e2c4f;
}
.dash-update-head {
  text-align: center;
  padding: 24px 24px 16px;
}
.dash-update-title {
  font-size: 18px;
  font-weight: 700;
  color: #e2e8f0;
  margin-top: 8px;
}
.dash-update-subtitle {
  font-size: 13px;
  color: #94a3b8;
  margin-top: 6px;
}
.dash-update-status {
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
  margin-top: 8px;
}

/* ===== top bar ===== */
.dash-topbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.dash-welcome-title {
  font-size: 16px;
  font-weight: 700;
  color: #e2e8f0;
}

.dash-welcome-subtitle {
  font-size: 12px;
  color: #94a3b8;
}

.dash-status,
.dash-clock {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 10px;
  background: #101b36;
  border: 1px solid #1e2c4f;
}

.dash-status-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #22c55e;
}

.dash-clock .dash-status-label {
  color: #93c5fd;
}

.dash-status-value {
  font-size: 11px;
  color: #cbd5e1;
}

/* ===== panel ===== */
.dash-panel {
  display: flex;
  flex-direction: column;
  background: #101b36;
  border: 1px solid #1e2c4f;
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
}

.dash-panel-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #1e2c4f;
}

.dash-panel-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #e2e8f0;
}

.dash-panel-link {
  font-size: 11px;
  font-weight: 600;
  color: #60a5fa;
  cursor: pointer;
}

.dash-panel-footer {
  margin-top: auto;
  text-align: center;
  padding: 8px;
  margin: auto 12px 12px 12px;
  border-radius: 8px;
  background: #16224384;
  border: 1px solid #1e2c4f;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dash-panel-footer:hover {
  color: #e2e8f0;
  border-color: #3b5689;
}

/* ===== peta ===== */
.dash-map-wrapper {
  padding: 12px;
}

.dash-map-stats {
  display: flex;
  gap: 8px;
  padding: 0 12px 12px 12px;
}

.dash-map-stat {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #0a1224;
  border: 1px solid #1e2c4f;
}

.dash-map-stat-label {
  font-size: 10px;
  color: #94a3b8;
}

.dash-map-stat-value {
  font-size: 16px;
  font-weight: 800;
  color: #e2e8f0;
}

.dash-map-stat-value.online {
  color: #22c55e;
}

.dash-map-stat-value.offline {
  color: #ef4444;
}

.dash-map-stat-value.gangguan {
  color: #f59e0b;
}

/* ===== live view ===== */
.dash-live-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 12px;
}

.dash-live-tile {
  border-radius: 10px;
  overflow: hidden;
  background: #0a1224;
  border: 1px solid #1e2c4f;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.dash-live-tile:hover,
.dash-live-tile--active {
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.35);
}

.dash-live-img {
  width: 100%;
  height: 110px;
  object-fit: cover;
  display: block;
}

.dash-live-placeholder {
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(160deg, #0e1830 0%, #122142 100%);
}

.dash-live-caption {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
}

.dash-live-name {
  font-size: 10px;
  font-weight: 600;
  color: #cbd5e1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ===== dot indikator ===== */
.dash-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dash-dot--green {
  background: #22c55e;
}

.dash-dot--red {
  background: #ef4444;
}

.dash-dot--amber {
  background: #f59e0b;
}

/* ===== statistik (donut) ===== */
.dash-donut-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px;
  gap: 18px;
}

.dash-donut {
  width: 160px;
  height: 160px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dash-donut-hole {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: #101b36;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.dash-donut-total {
  font-size: 26px;
  font-weight: 800;
  color: #e2e8f0;
  line-height: 1.1;
}

.dash-donut-label {
  font-size: 10px;
  color: #94a3b8;
}

.dash-legend {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dash-legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #94a3b8;
}

.dash-legend-item b {
  margin-left: auto;
  color: #e2e8f0;
}

/* ===== top 5 wilayah ===== */
.dash-top-list {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dash-top-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.dash-top-name {
  font-size: 12px;
  color: #cbd5e1;
}

.dash-top-area {
  font-size: 10px;
  color: #64748b;
  margin-bottom: 4px;
}

.dash-top-count {
  font-size: 12px;
  font-weight: 700;
  color: #e2e8f0;
}

.dash-top-bar {
  height: 5px;
  border-radius: 5px;
  background: #0a1224;
  overflow: hidden;
}

.dash-top-bar-fill {
  height: 100%;
  border-radius: 5px;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
}

.dash-top-bar-fill--access {
  background: linear-gradient(90deg, #0ea5e9, #22d3ee);
}

/* ===== peristiwa & gangguan ===== */
.dash-event-list {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dash-event-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #0a1224;
  border: 1px solid #1e2c4f;
}

.dash-event-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dash-event-text {
  min-width: 0;
}

.dash-event-title {
  font-size: 11px;
  font-weight: 700;
  color: #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-event-subtitle {
  font-size: 10px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-event-time {
  margin-left: auto;
  font-size: 9px;
  color: #ef4444;
  white-space: nowrap;
}

/* ===== empty state ===== */
.dash-empty {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 28px 16px;
  gap: 4px;
}

.dash-empty-cam {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #16224384;
  border: 1px solid #1e2c4f;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.dash-empty-title {
  font-size: 12px;
  font-weight: 700;
  color: #cbd5e1;
}

.dash-empty-subtitle {
  font-size: 11px;
  color: #64748b;
}

@media (max-width: 960px) {
  .dash-topbar {
    flex-wrap: wrap;
  }

  .dash-live-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
